import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import OpenPlayToolsLayout from '../../components/openplay/OpenPlayToolsLayout';
import StepIndicator from './components/StepIndicator';
import BasicInfoStep from './components/BasicInfoStep';
import PricingStep from './components/PricingStep';
import PreviewStep from './components/PreviewStep';
import CompletionStep from './components/CompletionStep';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { resizeImageToWidth } from '../../lib/imageUtils';

const SessionCreationWizard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editingId = searchParams.get('id');
  const isEditMode = Boolean(editingId);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    sport: 'pickleball',
    coverImageUrl: '',
    imageUrls: [],
    coverDraftId: '',
    imageDrafts: [],
    venue: '',
    venueAddress: '',
    venueLat: '',
    venueLng: '',
    date: '',
    endDate: '',
    startTime: '',
    endTime: '',
    maxParticipants: '',
    skillLevel: '',
    courtCount: '',
    description: '',
    pricingModel: 'fixed',
    memberPrice: '',
    bankName: '',
    accountNumber: '',
    accountHolder: '',
    paymentDeadline: '',
    registrationDeadline: '',
    chargeCancellationFee: false,
    cancellationFeePercent: '',
    participantVisibility: 'realname',
    showContactInfo: false,
    showSkillLevel: false,
    sendRegistrationConfirmation: true,
    sendPaymentReminder: true,
    reminderDaysBefore: '1',
    sendSessionStartReminder: true,
    minParticipants: '',
    enableRating: false,
    specialInstructions: '',
    enableKakaoShare: true,
    enableLinkCopy: true,
    autoGenerateAnnouncement: false,
    sendRegistrationNotification: false,
    sendPaymentConfirmationNotification: false,
    saveAsTemplate: false,
    createRecurringSession: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sessionCreated, setSessionCreated] = useState(false);
  const [createdSessionData, setCreatedSessionData] = useState(null);

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatusText, setUploadStatusText] = useState('');

  const [initialImageUrls, setInitialImageUrls] = useState([]);

  const lastAutoRegDeadlineRef = useRef('');
  const lastAutoPayDeadlineRef = useRef('');

  const [venueOptions, setVenueOptions] = useState([]);
  const [venuePaymentConfig, setVenuePaymentConfig] = useState(null);

  const loadVenueDefaults = async () => {
    try {
      const { data, error } = await supabase
        .from('venues')
        .select('id, name, facility_info, payment_config')
        .order('created_at', { ascending: true })
        .limit(1);

      if (error) {
        setVenueOptions([]);
        setVenuePaymentConfig(null);
        return;
      }

      const first = (data || [])?.[0];
      const facilityName = first?.facility_info?.name || first?.name;
      if (facilityName) {
        setVenueOptions([{ value: facilityName, label: facilityName }]);
      } else {
        setVenueOptions([]);
      }

      const pc = first?.payment_config || null;
      setVenuePaymentConfig(pc);
    } catch {
      setVenueOptions([]);
      setVenuePaymentConfig(null);
    }
  };

  useEffect(() => {
    loadVenueDefaults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentStep !== 0) return;
    loadVenueDefaults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  useEffect(() => {
    const handleFocus = () => {
      loadVenueDefaults();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadVenueDefaults();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const venueOptionsForSelect = useMemo(() => {
    if (venueOptions?.length > 0) return venueOptions;
    return [];
  }, [venueOptions]);

  const steps = [
    { id: 'basic', title: '기본 정보', icon: 'FileText' },
    { id: 'preview', title: '미리보기', icon: 'Eye' },
    { id: 'complete', title: '완료', icon: 'CheckCircle' }
  ];

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (!formData?.title?.trim()) newErrors.title = '제목을 입력하세요';
      const urlCount = Array.isArray(formData?.imageUrls) ? formData.imageUrls.length : 0;
      const draftCount = Array.isArray(formData?.imageDrafts) ? formData.imageDrafts.length : 0;
      if (urlCount + draftCount > 10) {
        newErrors.coverImageUrl = '이미지는 최대 10장까지 업로드할 수 있습니다';
      }
      if (!formData?.venue || !String(formData?.venueLat || '').trim() || !String(formData?.venueLng || '').trim()) {
        newErrors.venue = '네이버 검색으로 장소를 선택하세요';
      }
      if (!formData?.date) newErrors.date = '시작일시를 선택하세요';
      if (!formData?.startTime) newErrors.startTime = '시작일시를 선택하세요';
      if (!formData?.maxParticipants) newErrors.maxParticipants = '최대 참가 인원을 입력하세요';
      if (!formData?.skillLevel) newErrors.skillLevel = '참가 레벨을 선택하세요';
      if (!formData?.courtCount) newErrors.courtCount = '코트 수를 입력하세요';
      if (!formData?.registrationDeadline) newErrors.registrationDeadline = '시작일시를 선택하세요';

      if (!formData?.memberPrice) newErrors.memberPrice = '가격을 입력하세요';

      if (!formData?.bankName?.trim()) newErrors.bankName = '은행명을 입력하세요';
      if (!formData?.accountNumber?.trim()) newErrors.accountNumber = '계좌번호를 입력하세요';
      if (!formData?.accountHolder?.trim()) newErrors.accountHolder = '예금주를 입력하세요';
      if (!formData?.paymentDeadline) newErrors.paymentDeadline = '결제 마감 시간을 선택하세요';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep === steps?.length - 2) {
        handleSubmit();
      } else {
        setCurrentStep(prev => Math.min(prev + 1, steps?.length - 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStepClick = (stepIndex) => {
    if (stepIndex < currentStep) {
      setCurrentStep(stepIndex);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleFormDataChange = (newData) => {
    setFormData(newData);
  };

  const handleVenueSearch = async (query) => {
    const q = String(query || '').trim();
    if (!q) return [];

    const normalize = (rows) => {
      const list = Array.isArray(rows) ? rows : [];
      return list.map((r) => {
        const roadAddress = String(r?.roadAddress || '').trim();
        const jibunAddress = String(r?.jibunAddress || '').trim();
        const englishAddress = String(r?.englishAddress || '').trim();
        const x = r?.x != null ? Number(r.x) : null;
        const y = r?.y != null ? Number(r.y) : null;
        return {
          roadAddress: roadAddress || null,
          jibunAddress: jibunAddress || null,
          englishAddress: englishAddress || null,
          x: Number.isFinite(x) ? x : null,
          y: Number.isFinite(y) ? y : null,
          placeName: r?.placeName ? String(r.placeName) : null
        };
      });
    };

    // 1) Place keyword search (정석): 장소명/시설명에 강함
    try {
      const { data: placeData, error: placeError } = await supabase.functions.invoke('naver-place-search', {
        body: { query: q, display: 10 }
      });

      if (placeError) {
        let placeErrorDetails = null;
        try {
          if (placeError?.context && typeof placeError.context?.json === 'function') {
            placeErrorDetails = await placeError.context.json();
          }
        } catch {
          // ignore
        }

        console.error('[Wizard][naver-place-search] failed', {
          message: placeError?.message,
          status: placeError?.context?.status,
          details: placeErrorDetails,
        });

        const msg = String(placeErrorDetails?.error || '').trim();
        if (msg.includes('Missing Naver Search API keys')) {
          throw new Error('장소명 검색을 위한 네이버 Search API 키가 설정되지 않았습니다. (NAVER_SEARCH_CLIENT_ID / NAVER_SEARCH_CLIENT_SECRET)');
        }
      }

      if (placeData?.ok) {
        const normalized = normalize(placeData?.results);
        if (normalized.length > 0) return normalized;
      }
    } catch {
      // ignore and fallback to geocode
    }

    const { data, error } = await supabase.functions.invoke('naver-geocode', {
      body: { query: q, count: 10, language: 'kor' }
    });

    if (error) {
      let errorMessage = error?.message || '네이버 지도 검색에 실패했습니다.';
      let errorDetails = null;
      try {
        if (error?.context && typeof error.context?.json === 'function') {
          const errBody = await error.context.json();
          errorDetails = errBody;
          if (errBody?.error) {
            errorMessage = String(errBody.error);
          } else if (errBody?.details) {
            errorMessage = typeof errBody.details === 'string' ? errBody.details : JSON.stringify(errBody.details);
          } else {
            errorMessage = JSON.stringify(errBody);
          }
        }
      } catch {
        // ignore
      }

      console.error('[Wizard][naver-geocode] failed', {
        message: error?.message,
        status: error?.context?.status,
        details: errorDetails,
      });
      throw new Error(errorMessage);
    }

    if (!data?.ok) {
      throw new Error(data?.error || '네이버 지도 검색에 실패했습니다.');
    }

    return normalize(data?.results);
  };

  useEffect(() => {
    const date = String(formData?.date || '').trim();
    const startTime = String(formData?.startTime || '').trim();
    const endTime = String(formData?.endTime || '').trim();
    if (!date) return;
    const time = endTime || startTime;
    if (!time) return;

    const nextAuto = `${date}T${time}`;

    setFormData(prev => {
      const prevReg = String(prev?.registrationDeadline || '').trim();
      const prevPay = String(prev?.paymentDeadline || '').trim();

      const shouldUpdateReg = !prevReg || prevReg === lastAutoRegDeadlineRef.current;
      const shouldUpdatePay = !prevPay || prevPay === lastAutoPayDeadlineRef.current;
      if (!shouldUpdateReg && !shouldUpdatePay) return prev;

      const patched = { ...(prev || {}) };
      if (shouldUpdateReg) patched.registrationDeadline = nextAuto;
      if (shouldUpdatePay) patched.paymentDeadline = nextAuto;

      if (shouldUpdateReg) lastAutoRegDeadlineRef.current = nextAuto;
      if (shouldUpdatePay) lastAutoPayDeadlineRef.current = nextAuto;

      return patched;
    });
  }, [formData?.date, formData?.startTime, formData?.endTime]);

  useEffect(() => {
    const loadForEdit = async () => {
      if (!editingId) return;

      setIsSubmitting(true);
      setErrors({});
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData?.session?.user?.id;
        if (!userId) {
          alert('로그인이 필요합니다. 다시 로그인 후 시도해주세요.');
          return;
        }

        const { data, error } = await supabase
          .from('session_invites')
          .select('*')
          .eq('id', editingId)
          .eq('client_id', userId)
          .single();

        if (error) {
          alert(error?.message || '수정할 초대장 정보를 불러오지 못했습니다.');
          return;
        }

        const toDatetimeLocal = (value) => {
          if (!value) return '';
          try {
            return new Date(value).toISOString().slice(0, 16);
          } catch {
            return '';
          }
        };

        const loadedPaymentDeadline = toDatetimeLocal(data?.payment_deadline);
        const loadedRegistrationDeadline = toDatetimeLocal(data?.registration_deadline);
        lastAutoRegDeadlineRef.current = loadedRegistrationDeadline || '';
        lastAutoPayDeadlineRef.current = loadedPaymentDeadline || '';

        setFormData(prev => ({
          ...(prev || {}),
          title: data?.title || '',
          sport: data?.sport || '',
          coverImageUrl: String(data?.cover_image_url || '').trim(),
          imageUrls: Array.isArray(data?.image_urls) ? data.image_urls : [],
          coverDraftId: '',
          imageDrafts: [],
          venue: data?.venue || '',
          venueAddress: data?.venue_address || '',
          venueLat: data?.venue_lat != null ? String(data.venue_lat) : '',
          venueLng: data?.venue_lng != null ? String(data.venue_lng) : '',
          date: data?.date || '',
          endDate: data?.end_date || '',
          startTime: data?.start_time || '',
          endTime: data?.end_time || '',
          maxParticipants: String(data?.capacity ?? ''),
          courtCount: data?.court_count != null ? String(data.court_count) : '',
          skillLevel: data?.skill_level || '',
          description: data?.description || '',
          registrationDeadline: loadedRegistrationDeadline || '',
          paymentDeadline: loadedPaymentDeadline || '',
          pricingModel: 'fixed',
          memberPrice: data?.price != null ? String(data.price) : '',
          bankName: data?.bank_name || '',
          accountNumber: data?.account_number || '',
          accountHolder: data?.account_holder || ''
        }));

        setInitialImageUrls(Array.isArray(data?.image_urls) ? data.image_urls : []);
      } finally {
        setIsSubmitting(false);
      }
    };

    loadForEdit();
  }, [editingId]);

  const generateToken = () => {
    const arr = new Uint8Array(12);
    if (globalThis.crypto?.getRandomValues) {
      globalThis.crypto.getRandomValues(arr);
      return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
    }

    // Fallback (should be rare): include timestamp + Math.random to reduce collisions
    const fallback = `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
    return fallback.replace(/[^a-f0-9]/gi, '').slice(0, 24).padEnd(24, '0');
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) {
        alert('로그인이 필요합니다. 다시 로그인 후 시도해주세요.');
        return;
      }

      const getFileExt = (file) => {
        const type = String(file?.type || '').toLowerCase();
        if (type === 'image/webp') return 'webp';
        if (type === 'image/png') return 'png';
        if (type === 'image/jpeg' || type === 'image/jpg') return 'jpg';
        if (type === 'image/gif') return 'gif';
        return 'jpg';
      };

      const uuid = () => {
        return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      };

      const addHoursToLocalDatetime = (date, time, hours) => {
        const d = String(date || '').trim();
        const t = String(time || '').trim();
        if (!d || !t) return { date: '', time: '' };
        const base = new Date(`${d}T${t}`);
        if (Number.isNaN(base.getTime())) return { date: '', time: '' };
        base.setHours(base.getHours() + (Number(hours) || 0));
        const yyyy = base.getFullYear();
        const mm = String(base.getMonth() + 1).padStart(2, '0');
        const dd = String(base.getDate()).padStart(2, '0');
        const hh = String(base.getHours()).padStart(2, '0');
        const min = String(base.getMinutes()).padStart(2, '0');
        return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${min}` };
      };

      const venueLabel = String(formData?.venue || '').trim();

      const maxParticipants = parseInt(formData?.maxParticipants) || 0;
      const courtCount = Number.isFinite(parseInt(formData?.courtCount)) ? parseInt(formData.courtCount) : null;
      const fixedMemberPrice = parseInt(formData?.memberPrice) || 0;

      const regDeadlineLocal = String(formData?.registrationDeadline || '').trim();
      const autoRegDeadline = formData?.date && formData?.startTime ? `${formData.date}T${formData.startTime}` : '';
      const finalRegistrationDeadline = regDeadlineLocal || autoRegDeadline;

      const startDate = String(formData?.date || '').trim();
      const startTime = String(formData?.startTime || '').trim();
      const hasEnd = Boolean(String(formData?.endDate || '').trim() && String(formData?.endTime || '').trim());
      const computedEnd = !hasEnd && startDate && startTime ? addHoursToLocalDatetime(startDate, startTime, 2) : null;
      const finalEndDate = String(formData?.endDate || '').trim() || computedEnd?.date || startDate;
      const finalEndTime = String(formData?.endTime || '').trim() || computedEnd?.time || '';

      const patchPayload = {
        title: formData?.title,
        sport: formData?.sport,
        skill_level: formData?.skillLevel || null,
        description: String(formData?.description || '').trim() || null,
        date: formData?.date,
        end_date: finalEndDate,
        start_time: startTime,
        end_time: finalEndTime,
        venue: venueLabel || null,
        venue_address: String(formData?.venueAddress || '').trim() || null,
        venue_lat: Number.isFinite(parseFloat(formData?.venueLat)) ? parseFloat(formData.venueLat) : null,
        venue_lng: Number.isFinite(parseFloat(formData?.venueLng)) ? parseFloat(formData.venueLng) : null,
        capacity: maxParticipants,
        court_count: courtCount,
        registration_deadline: finalRegistrationDeadline ? new Date(finalRegistrationDeadline).toISOString() : null,
        payment_deadline: formData?.paymentDeadline ? new Date(formData.paymentDeadline).toISOString() : null,
        price: fixedMemberPrice,
        bank_name: formData?.bankName?.trim() || null,
        account_number: formData?.accountNumber?.trim() || null,
        account_holder: formData?.accountHolder?.trim() || null
      };

      const { data: created, error } = isEditMode
        ? await supabase
            .from('session_invites')
            .update(patchPayload)
            .eq('id', editingId)
            .eq('client_id', userId)
            .select()
            .single()
        : await supabase
            .from('session_invites')
            .insert({
              client_id: userId,
              ...patchPayload,
              registered: 0,
              participants: [],
              status: 'open',
              payment_status: 'pending',
              registration_phase: 'regular',
              token: generateToken()
            })
            .select()
            .single();

      if (error) {
        console.error('[Wizard] DB 오류:', error);
        console.error('[Wizard] 오류 메시지:', error?.message);
        console.error('[Wizard] 오류 코드:', error?.code);
        console.error('[Wizard] 오류 상세:', error?.details);
        alert(error?.message || (isEditMode ? '초대장 수정 중 오류가 발생했습니다. 다시 시도해주세요.' : '초대장 생성 중 오류가 발생했습니다. 다시 시도해주세요.'));
        return;
      }

      const existingUrls = Array.isArray(formData?.imageUrls) ? formData.imageUrls : [];
      const drafts = Array.isArray(formData?.imageDrafts) ? formData.imageDrafts : [];
      const coverDraftId = String(formData?.coverDraftId || '').trim();
      const coverUrlExisting = String(formData?.coverImageUrl || '').trim();
      const coverThumbUrlExisting = String(formData?.coverImageThumbUrl || '').trim();

      const extractStoragePath = (publicUrl) => {
        const url = String(publicUrl || '').trim();
        if (!url) return '';
        const marker = '/storage/v1/object/public/session-images/';
        const idx = url.indexOf(marker);
        if (idx < 0) return '';
        return url.slice(idx + marker.length);
      };

      let uploaded = [];
      if (drafts.length > 0) {
        setUploadModalOpen(true);
        setUploadProgress(0);
        setUploadStatusText('이미지 저장중...');

        const bucket = 'session-images';
        for (let i = 0; i < drafts.length; i += 1) {
          const d = drafts[i];
          const file = d?.file;
          if (!file) continue;

          setUploadStatusText(`이미지 저장중... (${i + 1}/${drafts.length})`);

          const ext = getFileExt(file);
          const path = `${userId}/sessions/${created.id}/${uuid()}.${ext}`;
          const { error: uploadError } = await supabase.storage.from(bucket).upload(path, file, {
            cacheControl: '3600',
            upsert: false,
            contentType: file?.type || 'image/jpeg'
          });

          if (uploadError) {
            setUploadModalOpen(false);
            throw new Error(uploadError?.message || '이미지 업로드에 실패했습니다.');
          }

          const { data: publicData } = supabase.storage.from(bucket).getPublicUrl(path);
          const publicUrl = String(publicData?.publicUrl || '').trim();
          if (!publicUrl) {
            setUploadModalOpen(false);
            throw new Error('이미지 URL 생성에 실패했습니다.');
          }

          uploaded.push({ id: String(d?.id || ''), url: publicUrl, file });

          const pct = Math.round(((i + 1) / drafts.length) * 100);
          setUploadProgress(pct);
        }
      }

      const uploadedUrls = uploaded.map((u) => u.url);
      const mergedUrls = [...existingUrls, ...uploadedUrls];

      const coverFromDraft = coverDraftId
        ? uploaded.find((u) => u.id === coverDraftId)?.url || ''
        : '';

      const finalCoverUrl = coverFromDraft || coverUrlExisting || mergedUrls[0] || null;

      const resolveCoverFile = () => {
        if (!finalCoverUrl) return null;
        if (coverDraftId) {
          const hit = uploaded.find((u) => u.id === coverDraftId);
          return hit?.file || null;
        }
        const match = uploaded.find((u) => String(u?.url || '').trim() === String(finalCoverUrl || '').trim());
        return match?.file || null;
      };

      const uploadCoverThumb = async (coverFile) => {
        if (!coverFile) return null;
        const thumbFile = await resizeImageToWidth(coverFile, {
          width: 100,
          mimeType: 'image/jpeg',
          quality: 0.82,
          fileName: 'thumb.jpg',
        });
        const thumbPath = `${userId}/sessions/${created.id}/thumb_${uuid()}.jpg`;
        const { error: thumbError } = await supabase.storage.from(bucket).upload(thumbPath, thumbFile, {
          cacheControl: '3600',
          upsert: false,
          contentType: 'image/jpeg',
        });
        if (thumbError) {
          throw new Error(thumbError?.message || '대표 이미지 썸네일 업로드에 실패했습니다.');
        }
        const { data: thumbPublic } = supabase.storage.from(bucket).getPublicUrl(thumbPath);
        return String(thumbPublic?.publicUrl || '').trim() || null;
      };

      const coverFile = resolveCoverFile();
      const finalCoverThumbUrl = coverFile ? await uploadCoverThumb(coverFile) : (coverThumbUrlExisting || null);

      const { error: patchImagesError } = await supabase
        .from('session_invites')
        .update({
          cover_image_url: finalCoverUrl,
          cover_image_thumb_url: finalCoverThumbUrl,
          image_urls: mergedUrls
        })
        .eq('id', created.id)
        .eq('client_id', userId);

      if (patchImagesError) {
        console.error('[Wizard] 이미지 업데이트 오류:', patchImagesError);
        setUploadModalOpen(false);
        alert(patchImagesError?.message || '이미지 업데이트에 실패했습니다. 다시 시도해주세요.');
        return;
      }

      const removedUrls = (initialImageUrls || []).filter((url) => !mergedUrls.includes(url));
      const removedStoragePaths = removedUrls.map(extractStoragePath).filter(Boolean);
      if (removedStoragePaths.length > 0) {
        await supabase.storage.from('session-images').remove(removedStoragePaths);
      }

      try {
        for (const d of drafts) {
          if (d?.previewUrl) URL.revokeObjectURL(d.previewUrl);
        }
      } catch {
        // ignore
      }

      setFormData((prev) => ({
        ...(prev || {}),
        imageUrls: mergedUrls,
        coverImageUrl: finalCoverUrl || '',
        coverImageThumbUrl: finalCoverThumbUrl || '',
        imageDrafts: [],
        coverDraftId: ''
      }));

      setInitialImageUrls(mergedUrls);

      if (drafts.length > 0) {
        setUploadProgress(100);
        setUploadStatusText('완료');
        setUploadModalOpen(false);
      }

      setCreatedSessionData({
        ...created,
        cover_image_url: finalCoverUrl,
        image_urls: mergedUrls
      });

      setSessionCreated(true);
      setCurrentStep(steps?.length - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('[Wizard] 예외 발생:', error);
      alert('생성 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    if (sessionCreated && currentStep === steps?.length - 1) {
      return <CompletionStep sessionData={createdSessionData} />;
    }

    switch (currentStep) {
      case 0:
        return (
          <>
            <BasicInfoStep
              formData={formData}
              onChange={handleFormDataChange}
              errors={errors}
              venueOptions={venueOptionsForSelect}
              onVenueSearch={handleVenueSearch}
            />
            <PricingStep
              formData={formData}
              onChange={handleFormDataChange}
              errors={errors}
              venuePaymentConfig={venuePaymentConfig}
            />

            <div className="bg-muted rounded-md p-4 md:p-6">
              <div className="flex items-start gap-3">
                <Icon name="Calendar" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-heading font-medium text-foreground mb-2">
                    일정 요약
                  </h4>
                  <div className="space-y-2 text-sm caption text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Icon name="MapPin" size={16} />
                      <span>{formData?.venue || '장소 미선택'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="Clock" size={16} />
                      <span>
                        {formData?.date || '날짜 미선택'} {formData?.startTime && formData?.endTime ? `${formData?.startTime} - ${formData?.endTime}` : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="Users" size={16} />
                      <span>최대 {formData?.maxParticipants || 0}명</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      case 1:
        return <PreviewStep formData={formData} />;
      default:
        return null;
    }
  };

  return (
    <OpenPlayToolsLayout userRole="player">
      {uploadModalOpen && (
        <div className="fixed inset-0 z-300 flex items-center justify-center px-4" aria-modal="true" role="dialog">
          <div className="fixed inset-0 bg-background/70 backdrop-blur-sm" aria-hidden="true" />
          <div className="relative z-10 w-full max-w-md bg-card border border-border rounded-xl shadow-2xl p-5">
            <h3 className="text-lg font-heading font-semibold text-foreground">이미지 저장중</h3>
            <p className="text-sm caption text-muted-foreground mt-1">{uploadStatusText || '업로드 중...'}</p>

            <div className="mt-4">
              <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${Math.min(100, Math.max(0, uploadProgress || 0))}%` }}
                />
              </div>
              <div className="mt-2 text-right text-sm font-medium text-foreground data-text">
                {Math.min(100, Math.max(0, uploadProgress || 0))}%
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="pt-6 md:pt-10">
        <StepIndicator
          steps={steps}
          currentStep={currentStep}
          onStepClick={handleStepClick}
        />

        <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="mb-6 md:mb-8">
            <button
              onClick={() => navigate('/session-list-dashboard/invite')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-athletic"
            >
              <Icon name="ArrowLeft" size={20} />
              <span className="text-sm font-medium">초대장 목록으로 돌아가기</span>
            </button>
          </div>

          {renderStepContent()}

          {!sessionCreated && (
            <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t border-border">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  iconName="ChevronLeft"
                  iconPosition="left"
                  onClick={handleBack}
                  disabled={isSubmitting}
                  fullWidth
                >
                  이전
                </Button>
              )}

              <Button
                variant="default"
                iconName={currentStep === steps?.length - 2 ? 'Check' : 'ChevronRight'}
                iconPosition="right"
                onClick={handleNext}
                loading={isSubmitting}
                fullWidth
              >
                {currentStep === steps?.length - 2 ? '생성' : '다음'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </OpenPlayToolsLayout>
  );
};

export default SessionCreationWizard;